/**
 * Pure-Three.js voxel renderer for Endfb Wastes.
 * No react-three-fiber to avoid the CRA dev-build reconciler issue.
 *
 * Mounts a WebGL canvas into the given container ref, runs an internal RAF
 * loop, and reads world state from stateRef on every frame. All identity is
 * by uid/mid/bid/lid so we re-use Object3D entries instead of recreating.
 */
import { useEffect, useRef } from "react";
import * as THREE from "three";

const VIEW_W = 1000;
const VIEW_H = 620;

function makeCubeMesh(scene, color, w, h, d) {
    const geo = new THREE.BoxGeometry(w, h, d);
    const mat = new THREE.MeshLambertMaterial({ color });
    const m = new THREE.Mesh(geo, mat);
    scene.add(m);
    return m;
}

// ----- 2007scape-style "low-poly faceted" character -----
// All geometry uses low-segment primitives + flatShading for that classic look.
const TIER_LOOK = {
    rusty:  { armor: 0x6e4a2a, accent: 0x8a5b34, helmet: false, cape: false, glow: 0 },  // peasant rags
    bronze: { armor: 0x8a5b34, accent: 0xa56b3d, helmet: true,  cape: false, glow: 0 },
    iron:   { armor: 0x9aa0a8, accent: 0xc4c9d1, helmet: true,  cape: false, glow: 0 },
    gold:   { armor: 0xd4af37, accent: 0xf5d76e, helmet: true,  cape: true,  glow: 1 },
};

const SKIN_TONE = 0xd4a570;

function flatLambert(color) {
    const m = new THREE.MeshLambertMaterial({ color, flatShading: true });
    return m;
}

// Build a billboarded text sprite using a canvas texture. Returns a THREE.Sprite.
// opts: { color, outline, bg, size, font, weight }
function makeTextSprite(text, opts = {}) {
    const color = opts.color || "#ffffff";
    const outline = opts.outline || "#000000";
    const bg = opts.bg || null;
    const fontSize = opts.size || 64;
    const fontFamily = opts.font || "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
    const weight = opts.weight || "900";
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 96;
    const ctx = canvas.getContext("2d");
    ctx.font = `${weight} ${fontSize}px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (bg) {
        ctx.fillStyle = bg;
        const m = ctx.measureText(text);
        const pad = 14;
        const w = m.width + pad * 2;
        const h = fontSize + pad;
        const x0 = (canvas.width - w) / 2;
        const y0 = (canvas.height - h) / 2;
        ctx.fillRect(x0, y0, w, h);
    }
    ctx.lineJoin = "round";
    ctx.lineWidth = 8;
    ctx.strokeStyle = outline;
    ctx.strokeText(text, canvas.width / 2, canvas.height / 2);
    ctx.fillStyle = color;
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(28, 11, 1);  // world units; tweak per use
    sprite.userData.canvas = canvas;
    sprite.userData.ctx = ctx;
    sprite.userData.tex = tex;
    return sprite;
}

// Re-render an existing text sprite's canvas (cheaper than rebuilding the sprite/material).
function updateTextSprite(sprite, text, opts = {}) {
    if (!sprite || !sprite.userData?.ctx) return;
    const canvas = sprite.userData.canvas;
    const ctx = sprite.userData.ctx;
    const color = opts.color || "#ffffff";
    const outline = opts.outline || "#000000";
    const bg = opts.bg || null;
    const fontSize = opts.size || 64;
    const fontFamily = opts.font || "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
    const weight = opts.weight || "900";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = `${weight} ${fontSize}px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (bg) {
        ctx.fillStyle = bg;
        const m = ctx.measureText(text);
        const pad = 14;
        const w = m.width + pad * 2;
        const h = fontSize + pad;
        const x0 = (canvas.width - w) / 2;
        const y0 = (canvas.height - h) / 2;
        ctx.fillRect(x0, y0, w, h);
    }
    ctx.lineJoin = "round";
    ctx.lineWidth = 8;
    ctx.strokeStyle = outline;
    ctx.strokeText(text, canvas.width / 2, canvas.height / 2);
    ctx.fillStyle = color;
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    sprite.userData.tex.needsUpdate = true;
}

function buildVoxelCharacter(scene, isMe, tier = "rusty", weapon = "bronze_sword", hat = "", neck = "") {
    const group = new THREE.Group();
    scene.add(group);
    const look = TIER_LOOK[tier] || TIER_LOOK.rusty;

    // Pants/legs are NOT tier-colored — they're plain cloth so feet & gait stay visible
    const pantsMat = flatLambert(0x2e2218);   // dark brown trousers
    const bootMat  = flatLambert(0x14110c);   // near-black shoes
    const hipY = 9;
    const legLen = 8.5;

    // Left leg pivot (swings around hip)
    const legL_pivot = new THREE.Group();
    legL_pivot.position.set(-3.5, hipY, 0);
    const legL_mesh = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.0, legLen, 6), pantsMat);
    legL_mesh.position.y = -legLen / 2;
    legL_pivot.add(legL_mesh);
    const footL = new THREE.Mesh(new THREE.BoxGeometry(4.6, 1.8, 6), bootMat);
    footL.position.set(0, -legLen - 0.7, 1.5);
    legL_pivot.add(footL);
    group.add(legL_pivot);

    // Right leg pivot
    const legR_pivot = new THREE.Group();
    legR_pivot.position.set(3.5, hipY, 0);
    const legR_mesh = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.0, legLen, 6), pantsMat);
    legR_mesh.position.y = -legLen / 2;
    legR_pivot.add(legR_mesh);
    const footR = new THREE.Mesh(new THREE.BoxGeometry(4.6, 1.8, 6), bootMat);
    footR.position.set(0, -legLen - 0.7, 1.5);
    legR_pivot.add(footR);
    group.add(legR_pivot);

    // Belt (short hip wrap in tier color so the armor still "owns" the waistline)
    const beltMat = flatLambert(look.accent);
    const belt = new THREE.Mesh(new THREE.CylinderGeometry(6, 6, 3, 8), beltMat);
    belt.position.set(0, hipY + 1, 0);
    group.add(belt);

    // Torso — slightly wider cylinder
    const torsoMat = flatLambert(look.armor);
    const torso = new THREE.Mesh(new THREE.CylinderGeometry(7, 7.5, 12, 8), torsoMat);
    torso.position.set(0, 18, 0);
    group.add(torso);

    // Chest accent stripe (tier color band)
    const accentMat = flatLambert(look.accent);
    const chest = new THREE.Mesh(new THREE.CylinderGeometry(7.2, 7.2, 3.5, 8), accentMat);
    chest.position.set(0, 21, 0);
    group.add(chest);

    // Arms — short cylinders dangling at sides
    const armGeom = new THREE.CylinderGeometry(2.2, 2.2, 11, 6);
    const armL_pivot = new THREE.Group();
    armL_pivot.position.set(-9, 22, 0);
    const armL_mesh = new THREE.Mesh(armGeom, torsoMat);
    armL_mesh.position.y = -5.5;
    armL_pivot.add(armL_mesh);
    const handMat = flatLambert(SKIN_TONE);
    const handL = new THREE.Mesh(new THREE.SphereGeometry(2.2, 6, 5), handMat);
    handL.position.y = -11.5;
    armL_pivot.add(handL);
    group.add(armL_pivot);

    const armR_pivot = new THREE.Group();
    armR_pivot.position.set(9, 22, 0);
    const armR_mesh = new THREE.Mesh(armGeom, torsoMat);
    armR_mesh.position.y = -5.5;
    armR_pivot.add(armR_mesh);
    const handR = new THREE.Mesh(new THREE.SphereGeometry(2.2, 6, 5), handMat);
    handR.position.y = -11.5;
    armR_pivot.add(handR);
    // Attach the equipped weapon to the right hand (defaults to bronze scimitar).
    // If the player is unarmed (weapon === ""), no sword is rendered.
    // Held blade-up at the side, slightly outward — readable in over-the-shoulder cam.
    const swordMesh = buildEquippedWeapon(weapon);
    if (swordMesh) {
        swordMesh.position.set(2.5, -11, 2.0);     // a bit outboard of the hand
        swordMesh.rotation.x = -0.15;              // gentle forward lean
        swordMesh.rotation.z = -0.18;              // outward tilt
        armR_pivot.add(swordMesh);
    }
    group.add(armR_pivot);

    // Head — slightly squashed sphere, low segments
    const headMat = flatLambert(SKIN_TONE);
    const head = new THREE.Mesh(new THREE.SphereGeometry(5, 8, 6), headMat);
    head.position.set(0, 29, 0);
    head.scale.set(1, 1.1, 1);
    group.add(head);

    // Eyes — small dark spheres
    const eyeMat = flatLambert(0x222222);
    const eyeGeom = new THREE.SphereGeometry(0.55, 5, 4);
    const eyeL = new THREE.Mesh(eyeGeom, eyeMat);
    eyeL.position.set(-1.7, 29.5, 4.7);
    group.add(eyeL);
    const eyeR = new THREE.Mesh(eyeGeom, eyeMat);
    eyeR.position.set(1.7, 29.5, 4.7);
    group.add(eyeR);

    // Helmet (RS-style domed cap)
    if (look.helmet) {
        const helmetMat = flatLambert(look.accent);
        const helmet = new THREE.Mesh(new THREE.SphereGeometry(5.6, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), helmetMat);
        helmet.position.set(0, 31, 0);
        group.add(helmet);
        const rim = new THREE.Mesh(new THREE.TorusGeometry(5.6, 0.6, 4, 12), flatLambert(0x222222));
        rim.position.set(0, 31, 0);
        rim.rotation.x = Math.PI / 2;
        group.add(rim);
    }

    // Party hat — sits above helmet/head
    if (hat === "party_hat") {
        const partyHat = buildPartyHat();
        partyHat.position.set(0, look.helmet ? 35 : 33, 0);
        group.add(partyHat);
    }

    // Necklace — at the chest above the torso
    if (neck === "ruby_necklace") {
        const necklace = buildRubyNecklace();
        necklace.position.set(0, 22, 0);
        group.add(necklace);
    }

    if (look.cape) {
        const capeMat = flatLambert(0x8b1d1d);
        const cape = new THREE.Mesh(new THREE.BoxGeometry(12, 18, 0.6), capeMat);
        cape.position.set(0, 16, -5);
        group.add(cape);
    }

    if (look.glow) {
        const light = new THREE.PointLight(0xf5d76e, 0.7, 120);
        light.position.set(0, 26, 0);
        group.add(light);
    }

    // HP bar (kept as basic billboard cubes — visible from any angle)
    const hpGroup = new THREE.Group();
    hpGroup.position.set(0, look.helmet ? 42 : 38, 0);
    const hpBg = new THREE.Mesh(
        new THREE.BoxGeometry(18, 3, 0.5),
        new THREE.MeshBasicMaterial({ color: 0x0a0a0a }),
    );
    hpGroup.add(hpBg);
    const hpFill = new THREE.Mesh(
        new THREE.BoxGeometry(16, 1.8, 1),
        new THREE.MeshBasicMaterial({ color: 0x22c55e }),
    );
    hpFill.position.z = 0.4;
    hpGroup.add(hpFill);
    group.add(hpGroup);

    // Combat level label floating above the HP bar (billboarded sprite)
    const levelLabel = makeTextSprite("Lv 1", {
        color: "#fde047",
        outline: "#1a1410",
        size: 60,
        weight: "900",
    });
    levelLabel.position.set(0, look.helmet ? 50 : 46, 0);
    levelLabel.scale.set(22, 8, 1);
    group.add(levelLabel);

    // Floating chat bubble (above the level label). Hidden by default.
    const chatBubble = makeTextSprite("", {
        color: "#ffffff",
        outline: "#1a1410",
        size: 50,
        weight: "700",
        bg: "rgba(0,0,0,0.55)",
    });
    chatBubble.position.set(0, look.helmet ? 64 : 60, 0);
    chatBubble.scale.set(32, 12, 1);
    chatBubble.visible = false;
    group.add(chatBubble);

    return {
        group, body: torso, chest, accent: chest, hpFill, hpBg, hpGroup, head, tier, weapon, hat, neck,
        legL: legL_pivot, legR: legR_pivot, armL: armL_pivot, armR: armR_pivot,
        levelLabel, level: 1,
        chatBubble, chatText: "",
        // Animation state for the run cycle
        gait: { phase: 0, speed: 0, prevX: 0, prevZ: 0 },
        // Melee swing animation state — set by parent when swing event arrives
        swing: { active: false, t: 0 },
        // Entity-pick metadata (used by the raycaster for click-to-attack)
        entityType: "player",
    };
}

function buildVoxelMob(scene, kind) {
    const group = new THREE.Group();
    scene.add(group);
    const isGhoul = kind === "ghoul";

    if (isGhoul) {
        // Zombie/skeleton-ish — gray-green humanoid
        const skinMat = flatLambert(0x8b9c7a);
        const robe = new THREE.Mesh(new THREE.CylinderGeometry(6, 7, 14, 7), flatLambert(0x4a3b2a));
        robe.position.set(0, 7, 0);
        group.add(robe);
        const torso = new THREE.Mesh(new THREE.CylinderGeometry(5.5, 6, 9, 7), flatLambert(0x6c5544));
        torso.position.set(0, 17, 0);
        group.add(torso);
        const armGeom = new THREE.CylinderGeometry(1.6, 1.6, 9, 5);
        const armL = new THREE.Mesh(armGeom, skinMat);
        armL.position.set(-7, 17, 0);
        armL.rotation.z = 0.25;
        group.add(armL);
        const armR = new THREE.Mesh(armGeom, skinMat);
        armR.position.set(7, 17, 0);
        armR.rotation.z = -0.25;
        group.add(armR);
        const head = new THREE.Mesh(new THREE.SphereGeometry(4.5, 7, 5), skinMat);
        head.position.set(0, 26, 0);
        group.add(head);
        // Glowing red eyes
        const eyeMat = new THREE.MeshBasicMaterial({ color: 0xff2222 });
        const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.55, 4, 3), eyeMat);
        eyeL.position.set(-1.5, 26.3, 4.2);
        group.add(eyeL);
        const eyeR = new THREE.Mesh(new THREE.SphereGeometry(0.55, 4, 3), eyeMat);
        eyeR.position.set(1.5, 26.3, 4.2);
        group.add(eyeR);
        var body = torso;
    } else {
        // Rat — elongated body, low to the ground, with tail
        const furMat = flatLambert(0x5a4d3e);
        const body = new THREE.Mesh(new THREE.CylinderGeometry(3.5, 4, 12, 6), furMat);
        body.rotation.z = Math.PI / 2;
        body.position.set(0, 4, 0);
        group.add(body);
        const head = new THREE.Mesh(new THREE.SphereGeometry(3, 6, 5), furMat);
        head.position.set(7, 5, 0);
        head.scale.set(1.2, 0.9, 0.9);
        group.add(head);
        // Ears
        const earMat = flatLambert(0x423424);
        const earGeom = new THREE.ConeGeometry(1.1, 1.5, 5);
        const earL = new THREE.Mesh(earGeom, earMat);
        earL.position.set(7, 7.5, -1.5);
        earL.rotation.x = -0.3;
        group.add(earL);
        const earR = new THREE.Mesh(earGeom, earMat);
        earR.position.set(7, 7.5, 1.5);
        earR.rotation.x = 0.3;
        group.add(earR);
        // Tail
        const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 1, 9, 5), flatLambert(0x7d6a55));
        tail.rotation.z = Math.PI / 2.2;
        tail.position.set(-8, 5, 0);
        group.add(tail);
        // Legs
        const legGeom = new THREE.CylinderGeometry(0.7, 0.7, 3, 4);
        const legMat = furMat;
        for (const [lx, lz] of [[-3, -2.5], [-3, 2.5], [3, -2.5], [3, 2.5]]) {
            const leg = new THREE.Mesh(legGeom, legMat);
            leg.position.set(lx, 1.5, lz);
            group.add(leg);
        }
        // Glowing eyes
        const eyeMat = new THREE.MeshBasicMaterial({ color: 0xff8800 });
        const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.35, 4, 3), eyeMat);
        eyeL.position.set(9, 5.5, -1);
        group.add(eyeL);
        const eyeR = new THREE.Mesh(new THREE.SphereGeometry(0.35, 4, 3), eyeMat);
        eyeR.position.set(9, 5.5, 1);
        group.add(eyeR);
        var body_ref = body;  // for return
    }

    const finalBody = isGhoul ? body : body_ref;

    // HP bar — show above the creature
    const hpGroup = new THREE.Group();
    hpGroup.position.set(0, isGhoul ? 36 : 14, 0);
    const hpBg = new THREE.Mesh(
        new THREE.BoxGeometry(16, 2.4, 0.4),
        new THREE.MeshBasicMaterial({ color: 0x0a0a0a }),
    );
    hpGroup.add(hpBg);
    const hpFill = new THREE.Mesh(
        new THREE.BoxGeometry(14, 1.6, 0.8),
        new THREE.MeshBasicMaterial({ color: 0x22c55e }),
    );
    hpFill.position.z = 0.3;
    hpGroup.add(hpFill);
    group.add(hpGroup);
    return { group, body: finalBody, hpFill, hpGroup };
}

function buildLoot(scene) {
    const group = new THREE.Group();
    scene.add(group);
    // GP pile: small stack of coin spheres (RS-style)
    const coinMat = new THREE.MeshLambertMaterial({
        color: 0xd4af37,
        emissive: 0xa0790a,
        emissiveIntensity: 0.3,
        flatShading: true,
    });
    const core = new THREE.Group();
    for (let i = 0; i < 5; i++) {
        const coin = new THREE.Mesh(new THREE.CylinderGeometry(3.5, 3.5, 0.8, 8), coinMat);
        coin.position.set(
            (Math.random() - 0.5) * 3,
            i * 0.9,
            (Math.random() - 0.5) * 3,
        );
        coin.rotation.y = Math.random() * Math.PI;
        core.add(coin);
    }
    core.position.y = 14;
    group.add(core);
    return { group, core, kind: "credits" };
}

// Build a 3D loot drop for a wearable item (weapon / hat / neck).
function buildItemLoot(scene, slot, itemId) {
    const group = new THREE.Group();
    scene.add(group);
    let mesh = null;
    if (slot === "weapon") {
        // Lay the sword/scimitar flat on the ground
        if (itemId === "iron_sword") {
            mesh = buildIronSword();
        } else if (itemId === "bronze_scimitar") {
            mesh = buildBronzeScimitar();
        } else {
            mesh = buildBronzeSword();
        }
        mesh.rotation.x = Math.PI / 2;   // flat on the floor
        mesh.position.y = 2;
        group.add(mesh);
    } else if (slot === "hat") {
        if (itemId === "party_hat") {
            mesh = buildPartyHat();
            mesh.position.y = 0;
            group.add(mesh);
        }
    } else if (slot === "neck") {
        if (itemId === "ruby_necklace") {
            mesh = buildRubyNecklace();
            mesh.position.y = 4;
            group.add(mesh);
        }
    }
    // Subtle "lift" so item floats just above the ground.
    return { group, core: mesh, kind: "item", slot, itemId };
}

// Bronze Scimitar — curved-blade variant. Larger + held blade-up so it
// reads clearly in the over-the-shoulder camera.
function buildBronzeScimitar() {
    const sword = new THREE.Group();
    const bladeMat = flatLambert(0xd97706);
    const edgeMat = flatLambert(0xfbbf24);   // golden highlight along the spine
    // Curved blade — 7 thick segments swept along an arc
    const segments = 7;
    let prev = null;
    for (let i = 0; i < segments; i++) {
        const seg = new THREE.Mesh(new THREE.BoxGeometry(1.8, 3.0, 1.0), bladeMat);
        const t = i / (segments - 1);
        seg.position.y = i * 2.7 + 1.0;
        seg.position.x = Math.sin(t * 1.0) * 2.2;
        seg.rotation.z = -t * 0.36;
        sword.add(seg);
        // Bright spine accent on top side
        const accent = new THREE.Mesh(new THREE.BoxGeometry(0.45, 3.0, 0.45), edgeMat);
        accent.position.copy(seg.position);
        accent.position.x += 1.0;
        accent.rotation.z = seg.rotation.z;
        sword.add(accent);
        prev = seg;
    }
    // Tip (a longer pointed cone)
    const tip = new THREE.Mesh(new THREE.ConeGeometry(1.2, 3.0, 4), bladeMat);
    tip.position.set(prev.position.x + 0.8, prev.position.y + 2.4, 0);
    tip.rotation.z = -0.55;
    sword.add(tip);
    // Crossguard (gold, wide)
    const guard = new THREE.Mesh(new THREE.BoxGeometry(7.0, 1.4, 2.0), flatLambert(0xfbbf24));
    guard.position.y = 0.3;
    sword.add(guard);
    // Grip (dark leather)
    const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 4.0, 6), flatLambert(0x3a2a18));
    grip.position.y = -2.0;
    sword.add(grip);
    // Pommel (gold sphere)
    const pommel = new THREE.Mesh(new THREE.SphereGeometry(1.3, 7, 6), flatLambert(0xfbbf24));
    pommel.position.y = -4.4;
    sword.add(pommel);
    return sword;
}

function buildBullet(scene, mine) {
    // Spell-like glowing orb instead of cube
    const mat = new THREE.MeshBasicMaterial({ color: mine ? 0xffe066 : 0xff6b6b });
    const m = new THREE.Mesh(new THREE.SphereGeometry(3, 6, 5), mat);
    m.position.y = 16;
    scene.add(m);
    return m;
}

// Iron sword mesh — held in the right hand of a player.
function buildIronSword() {
    const sword = new THREE.Group();
    // Blade — long thin tapered iron prism
    const bladeMat = flatLambert(0xc4c9d1);
    const blade = new THREE.Mesh(new THREE.BoxGeometry(1.2, 14, 0.5), bladeMat);
    blade.position.y = 7;
    sword.add(blade);
    // Tip
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.8, 2.4, 4), bladeMat);
    tip.position.y = 15.2;
    sword.add(tip);
    // Crossguard
    const guardMat = flatLambert(0x6b6f76);
    const guard = new THREE.Mesh(new THREE.BoxGeometry(5, 1, 1.4), guardMat);
    guard.position.y = 0.3;
    sword.add(guard);
    // Grip
    const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 3, 6), flatLambert(0x3a2a18));
    grip.position.y = -1.6;
    sword.add(grip);
    // Pommel
    const pommel = new THREE.Mesh(new THREE.SphereGeometry(0.9, 6, 5), guardMat);
    pommel.position.y = -3.4;
    sword.add(pommel);
    return sword;
}

// Bronze sword — default starting weapon. Same silhouette as iron but a warm tan/copper tone.
function buildBronzeSword() {
    const sword = new THREE.Group();
    const bladeMat = flatLambert(0xd97706);
    const blade = new THREE.Mesh(new THREE.BoxGeometry(1.1, 12, 0.5), bladeMat);
    blade.position.y = 6;
    sword.add(blade);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.7, 2.0, 4), bladeMat);
    tip.position.y = 13;
    sword.add(tip);
    const guardMat = flatLambert(0xb45309);
    const guard = new THREE.Mesh(new THREE.BoxGeometry(4.6, 1, 1.4), guardMat);
    guard.position.y = 0.3;
    sword.add(guard);
    const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.65, 0.65, 2.8, 6), flatLambert(0x3a2a18));
    grip.position.y = -1.5;
    sword.add(grip);
    const pommel = new THREE.Mesh(new THREE.SphereGeometry(0.85, 6, 5), guardMat);
    pommel.position.y = -3.2;
    sword.add(pommel);
    return sword;
}

// Returns a Three.Group containing the equipped weapon model, or null.
function buildEquippedWeapon(weapon) {
    if (weapon === "iron_sword") return buildIronSword();
    if (weapon === "bronze_scimitar") return buildBronzeScimitar();
    if (!weapon) return null;
    // Default to bronze sword for any legacy values
    return buildBronzeSword();
}

// Party hat — a cone sitting on top of the head.
function buildPartyHat() {
    const hat = new THREE.Group();
    const hatMat = flatLambert(0xfde047);   // bright yellow
    const cone = new THREE.Mesh(new THREE.ConeGeometry(4, 9, 8), hatMat);
    cone.position.y = 4.5;
    hat.add(cone);
    // Pom-pom on top
    const pom = new THREE.Mesh(new THREE.SphereGeometry(1.2, 6, 5), flatLambert(0xffffff));
    pom.position.y = 10;
    hat.add(pom);
    return hat;
}

// Ruby necklace — chain + ruby pendant worn at the chest.
function buildRubyNecklace() {
    const neck = new THREE.Group();
    // Chain (subtle gold torus around the neck)
    const chain = new THREE.Mesh(
        new THREE.TorusGeometry(4.2, 0.35, 4, 16),
        flatLambert(0xc7a44a),
    );
    chain.rotation.x = Math.PI / 2;
    chain.position.y = 0;
    neck.add(chain);
    // Pendant (red sphere)
    const pendant = new THREE.Mesh(
        new THREE.SphereGeometry(1.4, 7, 6),
        flatLambert(0xdc2626),
    );
    pendant.position.set(0, -3.5, 3.5);
    neck.add(pendant);
    return neck;
}

// Merchant NPC — a robed shopkeeper standing in safe town. Static, decorative.
function buildMerchant(scene, x, z) {
    const group = new THREE.Group();
    group.position.set(x, 0, z);
    group.rotation.y = Math.PI;  // face south (toward player spawns)
    scene.add(group);

    // Robe (long teal cloth)
    const robeMat = flatLambert(0x2f5e6b);
    const robe = new THREE.Mesh(new THREE.CylinderGeometry(7, 10, 24, 8), robeMat);
    robe.position.y = 12;
    group.add(robe);
    // Belt
    const belt = new THREE.Mesh(new THREE.CylinderGeometry(7.4, 7.4, 2, 8), flatLambert(0x6b4a2a));
    belt.position.y = 17;
    group.add(belt);
    // Torso accent (apron)
    const apron = new THREE.Mesh(new THREE.BoxGeometry(8, 10, 0.6), flatLambert(0xc9a64a));
    apron.position.set(0, 17, 6.5);
    group.add(apron);
    // Arms (wider for that "shopkeeper" silhouette)
    const armGeom = new THREE.CylinderGeometry(2.3, 2.3, 11, 6);
    const armL = new THREE.Mesh(armGeom, robeMat);
    armL.position.set(-8.5, 22, 0);
    armL.rotation.z = 0.2;
    group.add(armL);
    const armR = new THREE.Mesh(armGeom, robeMat);
    armR.position.set(8.5, 22, 0);
    armR.rotation.z = -0.2;
    group.add(armR);
    // Hands
    const handMat = flatLambert(SKIN_TONE);
    const handL = new THREE.Mesh(new THREE.SphereGeometry(2.2, 6, 5), handMat);
    handL.position.set(-10, 16.5, 0);
    group.add(handL);
    const handR = new THREE.Mesh(new THREE.SphereGeometry(2.2, 6, 5), handMat);
    handR.position.set(10, 16.5, 0);
    group.add(handR);
    // Head
    const head = new THREE.Mesh(new THREE.SphereGeometry(5.2, 8, 6), handMat);
    head.position.set(0, 30, 0);
    head.scale.set(1, 1.1, 1);
    group.add(head);
    // Beard (tan trapezoid)
    const beard = new THREE.Mesh(new THREE.BoxGeometry(6, 3.5, 4), flatLambert(0xb59370));
    beard.position.set(0, 27.2, 3.6);
    group.add(beard);
    // Eyes
    const eyeMat = flatLambert(0x222222);
    const eyeGeom = new THREE.SphereGeometry(0.55, 5, 4);
    const eyeL = new THREE.Mesh(eyeGeom, eyeMat);
    eyeL.position.set(-1.7, 30.5, 4.7);
    group.add(eyeL);
    const eyeR = new THREE.Mesh(eyeGeom, eyeMat);
    eyeR.position.set(1.7, 30.5, 4.7);
    group.add(eyeR);
    // Wide-brim hat (Wizard / shopkeeper)
    const hatBrim = new THREE.Mesh(new THREE.CylinderGeometry(8, 8, 0.8, 8), flatLambert(0x4a321b));
    hatBrim.position.set(0, 34.5, 0);
    group.add(hatBrim);
    const hatCone = new THREE.Mesh(new THREE.ConeGeometry(5.5, 9, 8), flatLambert(0x4a321b));
    hatCone.position.set(0, 40, 0);
    group.add(hatCone);

    // Wooden stall in front of the merchant (counter + supporting posts + sign)
    const counterMat = flatLambert(0x6b4a2a);
    const counter = new THREE.Mesh(new THREE.BoxGeometry(46, 8, 14), counterMat);
    counter.position.set(0, 4, -16);
    group.add(counter);
    const postMat = flatLambert(0x4a321b);
    for (const [px] of [[-22], [22]]) {
        const post = new THREE.Mesh(new THREE.CylinderGeometry(1.8, 1.8, 28, 6), postMat);
        post.position.set(px, 14, -22);
        group.add(post);
    }
    // Awning roof
    const awning = new THREE.Mesh(
        new THREE.BoxGeometry(50, 1.5, 18),
        flatLambert(0x8b1d1d),
    );
    awning.position.set(0, 28, -19);
    awning.rotation.x = -0.1;
    group.add(awning);
    // Sign plank
    const sign = new THREE.Mesh(
        new THREE.BoxGeometry(28, 5, 1),
        flatLambert(0xc9a64a),
    );
    sign.position.set(0, 22, -24.5);
    group.add(sign);
    // Display items on counter (a tiny sword + a tiny shield)
    const swordOnCounter = buildIronSword();
    swordOnCounter.scale.set(1.1, 1.1, 1.1);
    swordOnCounter.rotation.z = Math.PI / 2;
    swordOnCounter.position.set(-12, 10, -16);
    group.add(swordOnCounter);
    const shield = new THREE.Mesh(
        new THREE.CylinderGeometry(4, 4, 0.6, 8),
        flatLambert(0x9aa0a8),
    );
    shield.rotation.x = Math.PI / 2;
    shield.position.set(12, 10, -16);
    group.add(shield);
    return group;
}

function buildWorld(scene, world) {
    const objects = [];
    // RuneScape-ish palette: warm grass / dusty wilderness / weathered cobbles.
    const COL_WILD_DIRT = 0x6b4d2a;      // wilderness ground (warm dirt)
    const COL_PLAINS    = 0x5e8a3a;      // grassy plains (bright sage green)
    const COL_PLAINS_2  = 0x4e7531;      // alternating tile shade
    const COL_STONE     = 0xb5ad9e;      // cobblestone safe town floor (warm tan)
    const COL_STONE_2   = 0x9a948b;      // alternating cobble shade
    const COL_WALL      = 0x6b5b48;      // stone wall (warmer brown)
    const COL_BORDER    = 0x3b2a18;      // dark earth at world edge

    const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(world.w, world.h),
        new THREE.MeshLambertMaterial({ color: COL_WILD_DIRT, flatShading: true }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(world.w / 2, -0.5, world.h / 2);
    scene.add(floor);
    objects.push(floor);

    const [sx1, sy1, sx2, sy2] = world.safe;
    const safeW = sx2 - sx1, safeD = sy2 - sy1;
    const cx = (sx1 + sx2) / 2, cz = (sy1 + sy2) / 2;
    // Plains ring (mossy green between dirt and town)
    const plains = new THREE.Mesh(
        new THREE.PlaneGeometry(safeW + 800, safeD + 800),
        new THREE.MeshLambertMaterial({ color: COL_PLAINS, flatShading: true }),
    );
    plains.rotation.x = -Math.PI / 2;
    plains.position.set(cx, 0, cz);
    scene.add(plains);
    objects.push(plains);
    // Scatter darker grass tiles for variation (RS-style mottled ground)
    const grassTileMat = new THREE.MeshLambertMaterial({ color: COL_PLAINS_2, flatShading: true });
    for (let i = 0; i < 40; i++) {
        const tw = 60 + Math.random() * 80;
        const angle = Math.random() * Math.PI * 2;
        const radius = (safeW / 2 + 60) + Math.random() * 320;
        const tx = cx + Math.cos(angle) * radius;
        const tz = cz + Math.sin(angle) * radius;
        const tile = new THREE.Mesh(new THREE.PlaneGeometry(tw, tw), grassTileMat);
        tile.rotation.x = -Math.PI / 2;
        tile.position.set(tx, 0.2, tz);
        tile.rotation.z = Math.random() * Math.PI / 2;
        scene.add(tile);
        objects.push(tile);
    }
    // Safe town floor — cobbled stone
    const safe = new THREE.Mesh(
        new THREE.PlaneGeometry(safeW, safeD),
        new THREE.MeshLambertMaterial({ color: COL_STONE, flatShading: true }),
    );
    safe.rotation.x = -Math.PI / 2;
    safe.position.set(cx, 0.5, cz);
    scene.add(safe);
    objects.push(safe);
    // Cobblestone tile pattern (checkerboard of small darker squares)
    const cobMat = new THREE.MeshLambertMaterial({ color: COL_STONE_2, flatShading: true });
    const tileSz = 40;
    const cols = Math.floor(safeW / tileSz);
    const rows = Math.floor(safeD / tileSz);
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            if ((r + c) % 2 !== 0) continue;
            const tile = new THREE.Mesh(new THREE.PlaneGeometry(tileSz - 2, tileSz - 2), cobMat);
            tile.rotation.x = -Math.PI / 2;
            tile.position.set(sx1 + (c + 0.5) * tileSz, 0.6, sy1 + (r + 0.5) * tileSz);
            scene.add(tile);
            objects.push(tile);
        }
    }
    // Safe town stone walls
    const wallMat = new THREE.MeshLambertMaterial({ color: COL_WALL, flatShading: true });
    const buildWall = (x, z, w, d, h) => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wallMat);
        m.position.set(x + w / 2, h / 2, z + d / 2);
        scene.add(m);
        objects.push(m);
    };
    buildWall(sx1, sy1, safeW, 4, 14);
    buildWall(sx1, sy2 - 4, safeW, 4, 14);
    buildWall(sx1, sy1, 4, safeD, 14);
    buildWall(sx2 - 4, sy1, 4, safeD, 14);
    // Crenellation caps on the walls (RS-style castle look)
    const capMat = new THREE.MeshLambertMaterial({ color: COL_STONE, flatShading: true });
    const buildCaps = (x, z, w, d, axis) => {
        const span = axis === "x" ? w : d;
        const step = 24;
        for (let i = 8; i < span - 8; i += step) {
            const cap = new THREE.Mesh(new THREE.BoxGeometry(8, 6, 8), capMat);
            if (axis === "x") cap.position.set(x + i, 17, z + d / 2);
            else cap.position.set(x + w / 2, 17, z + i);
            scene.add(cap);
            objects.push(cap);
        }
    };
    buildCaps(sx1, sy1, safeW, 4, "x");
    buildCaps(sx1, sy2 - 4, safeW, 4, "x");
    buildCaps(sx1, sy1, 4, safeD, "z");
    buildCaps(sx2 - 4, sy1, 4, safeD, "z");
    // World border (low rampart)
    const borderMat = new THREE.MeshLambertMaterial({ color: COL_BORDER, flatShading: true });
    const buildBorder = (x, z, w, d, h) => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), borderMat);
        m.position.set(x + w / 2, h / 2, z + d / 2);
        scene.add(m);
        objects.push(m);
    };
    buildBorder(0, 0, world.w, 4, 14);
    buildBorder(0, world.h - 4, world.w, 4, 14);
    buildBorder(0, 0, 4, world.h, 14);
    buildBorder(world.w - 4, 0, 4, world.h, 14);

    // Bank — medieval kiosk with wooden roof
    if (world.bank) {
        const [bx1, by1, bx2, by2] = world.bank;
        const bw = bx2 - bx1, bd = by2 - by1;
        const bcx = (bx1 + bx2) / 2, bcz = (by1 + by2) / 2;
        const counterMat = new THREE.MeshLambertMaterial({ color: 0x6b4a2a, flatShading: true });
        const counter = new THREE.Mesh(new THREE.BoxGeometry(bw, 6, bd), counterMat);
        counter.position.set(bcx, 3, bcz);
        scene.add(counter);
        objects.push(counter);
        // Wooden posts
        const postMat = new THREE.MeshLambertMaterial({ color: 0x4a321b, flatShading: true });
        for (const [px, pz] of [[-bw / 2, -bd / 2], [bw / 2, -bd / 2], [-bw / 2, bd / 2], [bw / 2, bd / 2]]) {
            const post = new THREE.Mesh(new THREE.CylinderGeometry(2.5, 2.5, 28, 6), postMat);
            post.position.set(bcx + px, 14, bcz + pz);
            scene.add(post);
            objects.push(post);
        }
        // Pitched wooden roof
        const roofMat = new THREE.MeshLambertMaterial({ color: 0x8b1d1d, flatShading: true });
        const roof = new THREE.Mesh(new THREE.ConeGeometry(bw * 0.9, 12, 4), roofMat);
        roof.position.set(bcx, 34, bcz);
        roof.rotation.y = Math.PI / 4;
        scene.add(roof);
        objects.push(roof);
        // Sign plank
        const sign = new THREE.Mesh(
            new THREE.BoxGeometry(bw + 4, 4, 1),
            new THREE.MeshLambertMaterial({ color: 0xc9a64a, flatShading: true }),
        );
        sign.position.set(bcx, 26, bcz + bd / 2 + 0.6);
        scene.add(sign);
        objects.push(sign);
    }

    // RuneScape-ish trees & rocks
    let seed = 1337;
    const rnd = () => {
        seed = (seed * 9301 + 49297) % 233280;
        return seed / 233280;
    };
    const trunkMat = new THREE.MeshLambertMaterial({ color: 0x5a3c1e, flatShading: true });
    const leafMatA = new THREE.MeshLambertMaterial({ color: 0x4f7a3a, flatShading: true });
    const leafMatB = new THREE.MeshLambertMaterial({ color: 0x3d6a2c, flatShading: true });
    const rockA = new THREE.MeshLambertMaterial({ color: 0x6e6964, flatShading: true });
    const rockB = new THREE.MeshLambertMaterial({ color: 0x877f78, flatShading: true });
    for (let i = 0; i < 130; i++) {
        const x = rnd() * world.w;
        const z = rnd() * world.h;
        if (x > sx1 && x < sx2 && z > sy1 && z < sy2) continue;
        const s = 0.7 + rnd() * 0.9;
        const yaw = rnd() * Math.PI;
        const g = new THREE.Group();
        g.position.set(x, 0, z);
        g.rotation.y = yaw;
        g.scale.set(s, s, s);
        if (rnd() > 0.55) {
            // RuneScape-style tree: cylindrical trunk + 2 sphere canopies
            const trunk = new THREE.Mesh(new THREE.CylinderGeometry(2.5, 3.5, 22, 6), trunkMat);
            trunk.position.y = 11;
            g.add(trunk);
            const leaf1 = new THREE.Mesh(new THREE.SphereGeometry(11, 7, 5), leafMatA);
            leaf1.position.y = 28;
            g.add(leaf1);
            const leaf2 = new THREE.Mesh(new THREE.SphereGeometry(7, 6, 5), leafMatB);
            leaf2.position.set(4, 34, 2);
            g.add(leaf2);
        } else {
            // Rough boulder pair
            const r1 = new THREE.Mesh(new THREE.DodecahedronGeometry(10, 0), rockA);
            r1.position.y = 6;
            r1.scale.set(1.2, 0.7, 1.1);
            g.add(r1);
            const r2 = new THREE.Mesh(new THREE.DodecahedronGeometry(5, 0), rockB);
            r2.position.set(6, 11, 3);
            g.add(r2);
        }
        scene.add(g);
        objects.push(g);
    }
    return objects;
}

export default function VoxelScene({ stateRef, aimRef, cameraYawRef, pickerRef, clickMarkerRef, entityPickerRef, attackTargetRef }) {
    const containerRef = useRef(null);
    const liveRef = useRef({ alive: true });

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        // Reset liveness flag (StrictMode mounts the effect twice in dev)
        liveRef.current = { alive: true };
        const live = liveRef.current;
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        const setSize = () => {
            const rect = container.getBoundingClientRect();
            renderer.setSize(rect.width, rect.height, false);
            renderer.domElement.style.width = "100%";
            renderer.domElement.style.height = "100%";
            renderer.domElement.style.display = "block";
        };
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        container.appendChild(renderer.domElement);
        setSize();
        const onResize = () => setSize();
        window.addEventListener("resize", onResize);

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xc8c8a8);   // warm dusty RS-style horizon
        scene.fog = new THREE.Fog(0xc8c8a8, 700, 2000);  // depth fog matches sky

        const camera = new THREE.PerspectiveCamera(55, VIEW_W / VIEW_H, 1, 6000);
        camera.position.set(1500, 200, 1600);

        // Picker: convert canvas-relative click coords -> world ground XZ.
        // Uses a Raycaster against a horizontal plane at y=0.
        const raycaster = new THREE.Raycaster();
        const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        const ndc = new THREE.Vector2();
        const hit = new THREE.Vector3();
        const pickGround = (canvasX, canvasY) => {
            const rect = container.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return null;
            ndc.x = (canvasX / rect.width) * 2 - 1;
            ndc.y = -(canvasY / rect.height) * 2 + 1;
            raycaster.setFromCamera(ndc, camera);
            const ok = raycaster.ray.intersectPlane(groundPlane, hit);
            if (!ok) return null;
            return { x: hit.x, y: hit.z };
        };
        if (pickerRef) pickerRef.current = pickGround;

        // Entity picker — finds the nearest player/mob group under the click.
        // Returns {type: "player"|"mob", id: string} or null.
        const pickEntity = (canvasX, canvasY) => {
            const rect = container.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return null;
            ndc.x = (canvasX / rect.width) * 2 - 1;
            ndc.y = -(canvasY / rect.height) * 2 + 1;
            raycaster.setFromCamera(ndc, camera);
            // Build a candidate set from all registered entity groups (players + mobs).
            // Each group has children we need recursive intersection on.
            const candidates = [];
            for (const [, h] of players) candidates.push(h.group);
            for (const [, h] of mobs) candidates.push(h.group);
            if (candidates.length === 0) return null;
            const hits = raycaster.intersectObjects(candidates, true);
            if (hits.length === 0) return null;
            // Walk up the parent chain to find a group with entity userData.
            for (const hres of hits) {
                let obj = hres.object;
                while (obj) {
                    const ud = obj.userData;
                    if (ud && ud.entityType && ud.entityId) {
                        return { type: ud.entityType, id: ud.entityId };
                    }
                    obj = obj.parent;
                }
            }
            return null;
        };
        if (entityPickerRef) entityPickerRef.current = pickEntity;

        // Click destination marker — a flat yellow ring on the ground that pulses.
        const markerGeom = new THREE.RingGeometry(10, 14, 24);
        const markerMat = new THREE.MeshBasicMaterial({
            color: 0xfde047, transparent: true, opacity: 0.9, side: THREE.DoubleSide,
            depthTest: false, depthWrite: false,
        });
        const clickMarker = new THREE.Mesh(markerGeom, markerMat);
        clickMarker.rotation.x = -Math.PI / 2;
        clickMarker.position.set(0, 0.6, 0);
        clickMarker.visible = false;
        scene.add(clickMarker);

        // Attack target marker — a red ring rendered under the currently-targeted entity.
        const targetMarkerMat = new THREE.MeshBasicMaterial({
            color: 0xef4444, transparent: true, opacity: 0.95, side: THREE.DoubleSide,
            depthTest: false, depthWrite: false,
        });
        const targetMarker = new THREE.Mesh(new THREE.RingGeometry(14, 19, 24), targetMarkerMat);
        targetMarker.rotation.x = -Math.PI / 2;
        targetMarker.position.set(0, 0.7, 0);
        targetMarker.visible = false;
        scene.add(targetMarker);

        // Lights
        scene.add(new THREE.AmbientLight(0x8a8e7c, 0.45));        // soft greenish-gray ambient
        const sun = new THREE.DirectionalLight(0xfff2c8, 0.85);    // warm RS sunlight
        sun.position.set(1500, 1800, 1200);
        scene.add(sun);
        const hemi = new THREE.HemisphereLight(0xc8c8a8, 0x3a4d28, 0.55);
        scene.add(hemi);

        // World built lazily once first state arrives
        let worldBuilt = false;

        // Entity registries
        const players = new Map(); // uid -> handle
        const mobs = new Map();
        const bullets = new Map();
        const loots = new Map();
        const hits = new Map();    // eid -> {sprite, age, maxAge}
        let me = null;

        const cleanup = () => {
            live.alive = false;
            window.removeEventListener("resize", onResize);
            if (pickerRef) pickerRef.current = null;
            if (entityPickerRef) entityPickerRef.current = null;
            try { container.removeChild(renderer.domElement); } catch { /* ignore */ }
            renderer.dispose();
        };

        const tempVec = new THREE.Vector3();
        // Drive a simple bipedal walk cycle + melee swing on a character handle.
        // Speed is measured from the world-position delta so it works for both
        // the lerped local player and remote players. If a swing is active,
        // the right arm overrides the walk-cycle motion with a forward strike.
        const updateGait = (h, dt) => {
            if (!h || !h.legL || !h.legR) return;
            const dx = h.group.position.x - h.gait.prevX;
            const dz = h.group.position.z - h.gait.prevZ;
            h.gait.prevX = h.group.position.x;
            h.gait.prevZ = h.group.position.z;
            const speed = dt > 0 ? Math.sqrt(dx * dx + dz * dz) / dt : 0;
            h.gait.speed += (speed - h.gait.speed) * Math.min(1, dt * 8);
            const moving = h.gait.speed > 8;
            const stepRate = moving ? Math.min(12, h.gait.speed * 0.05) : 0;
            if (moving) {
                h.gait.phase = (h.gait.phase + dt * stepRate) % (Math.PI * 2);
            } else {
                h.gait.phase += (0 - h.gait.phase) * Math.min(1, dt * 6);
            }
            const swing = moving ? 0.55 : 0;
            const s1 = Math.sin(h.gait.phase) * swing;
            const s2 = -s1;
            h.legL.rotation.x = s1;
            h.legR.rotation.x = s2;
            // Left arm always counter-swings to the walk cycle
            if (h.armL) h.armL.rotation.x = -s1 * 0.7;
            // Right arm: melee swing overrides the walk if active
            if (h.swing && h.swing.active) {
                h.swing.t += dt;
                const SWING_DUR = 0.42;
                if (h.swing.t >= SWING_DUR) {
                    h.swing.active = false;
                    h.swing.t = 0;
                    if (h.armR) h.armR.rotation.x = 0;
                    if (h.armR) h.armR.rotation.z = 0;
                } else {
                    // Wind-up (back) -> strike (forward) -> recover. Big visible arc.
                    const u = h.swing.t / SWING_DUR;          // 0..1
                    // Two-phase: wind back 0..0.25, big chop 0.25..1.0
                    let angle;
                    if (u < 0.25) {
                        angle = (u / 0.25) * 0.9;             // back-swing +0.9 rad
                    } else {
                        const v = (u - 0.25) / 0.75;          // 0..1 over chop phase
                        angle = 0.9 - Math.sin(v * Math.PI) * 2.6;  // peak forward at v=0.5 (~-1.7 rad)
                    }
                    if (h.armR) {
                        h.armR.rotation.x = angle;
                        // Slight twist toward target for chop flair
                        h.armR.rotation.z = Math.sin(u * Math.PI) * -0.4;
                    }
                }
            } else if (h.armR) {
                h.armR.rotation.x = -s2 * 0.7;
                h.armR.rotation.z = 0;
            }
            // Subtle vertical bob so the body has weight
            const bob = moving ? Math.abs(Math.sin(h.gait.phase * 2)) * 0.6 : 0;
            h.group.position.y = bob;
        };

        // Apply a brief hit-flash on a character/mob handle: a scale punch +
        // recoil push. Called every frame; advances h.hitFlash.t until done.
        const updateHitFlash = (h, dt) => {
            if (!h || !h.hitFlash) return;
            h.hitFlash.t += dt;
            const u = h.hitFlash.t / h.hitFlash.dur;
            if (u >= 1) {
                h.group.scale.set(1, 1, 1);
                h.hitFlash = null;
                return;
            }
            // Scale-pop: bigger at impact, settle back
            const punch = 1 + Math.sin(u * Math.PI) * 0.18;
            h.group.scale.set(punch, punch, punch);
        };

        // Update a character handle's chat bubble based on the server payload.
        const updateChatBubble = (h, chat) => {
            if (!h || !h.chatBubble) return;
            if (chat && chat.text) {
                if (h.chatText !== chat.text) {
                    updateTextSprite(h.chatBubble, chat.text, {
                        color: "#ffffff", outline: "#1a1410", size: 50, weight: "700",
                        bg: "rgba(0,0,0,0.55)",
                    });
                    h.chatText = chat.text;
                    // Dynamic width based on text length so the bubble doesn't crop
                    const w = Math.min(60, Math.max(22, chat.text.length * 1.6 + 14));
                    h.chatBubble.scale.set(w, 12, 1);
                }
                h.chatBubble.visible = true;
            } else {
                h.chatBubble.visible = false;
                h.chatText = "";
            }
        };

        // Last seen swing_age for each player; used to detect new swing events.
        const lastSwingAge = new Map();   // uid -> last swing_age value
        const triggerSwing = (h) => {
            if (!h || !h.swing) return;
            h.swing.active = true;
            h.swing.t = 0;
        };

        let lastFrame = performance.now();
        const animate = () => {
            if (!live.alive) return;
            const now = performance.now();
            const dt = (now - lastFrame) / 1000;
            lastFrame = now;
            const s = stateRef.current;
            if (s?.world && !worldBuilt) {
                buildWorld(scene, s.world);
                // Visible armoury merchant NPC in safe town (south-west of the bank).
                const [sx1, sy1, sx2, sy2] = s.world.safe;
                const merchantX = (sx1 * 0.6 + sx2 * 0.4);  // a bit left of center
                const merchantZ = sy1 + (sy2 - sy1) * 0.22; // upper area of safe town
                buildMerchant(scene, merchantX, merchantZ);
                worldBuilt = true;
            }
            if (s?.you) {
                // Rebuild my character when tier/weapon/hat/neck changes
                const myTier = s.you.tier || "rusty";
                const myWeapon = s.you.weapon || "bronze_sword";
                const myHat = s.you.hat || "";
                const myNeck = s.you.neck || "";
                if (!me || me.tier !== myTier || me.weapon !== myWeapon || me.hat !== myHat || me.neck !== myNeck) {
                    if (me) scene.remove(me.group);
                    me = buildVoxelCharacter(scene, true, myTier, myWeapon, myHat, myNeck);
                    me.group.userData = { entityType: "player", entityId: s.you.uid };
                    me.group.position.set(s.you.x, 0, s.you.y);
                }
                // Smooth lerp toward latest server position (feels responsive at 20Hz broadcast)
                const meAlpha = 1 - Math.pow(0.001, dt);  // dt-aware exp smoothing
                me.group.position.x += (s.you.x - me.group.position.x) * meAlpha;
                me.group.position.z += (s.you.y - me.group.position.z) * meAlpha;
                const hp01 = Math.max(0, s.you.hp / (s.you.max_hp || 100));
                me.hpFill.scale.x = Math.max(0.001, hp01);
                me.hpFill.position.x = (16 * hp01 - 16) / 2;
                me.hpFill.material.color.setHex(hp01 < 0.3 ? 0xef4444 : 0x22c55e);
                me.group.visible = s.you.alive;
                // Detect a fresh melee swing (swing_age dropped vs last frame)
                {
                    const cur = s.you.swing_age ?? 999;
                    const prev = lastSwingAge.get("__me__") ?? 999;
                    if (cur < prev - 0.05 && cur < 0.4) triggerSwing(me);
                    lastSwingAge.set("__me__", cur);
                }
                // Update level label when level changes
                const myLevel = s.you.combat_level || 1;
                if (me.level !== myLevel) {
                    updateTextSprite(me.levelLabel, `Lv ${myLevel}`, {
                        color: "#fde047", outline: "#1a1410", size: 60, weight: "900",
                    });
                    me.level = myLevel;
                }
                updateGait(me, dt);
                updateHitFlash(me, dt);
                updateChatBubble(me, s.you.chat);

                // Camera (over-the-shoulder). Yaw is driven by A/D keys via cameraYawRef.
                const yaw = (cameraYawRef && typeof cameraYawRef.current === "number") ? cameraYawRef.current : 0;
                const dist = 150;
                const height = 130;
                const camX = me.group.position.x + Math.sin(yaw) * -dist;
                const camZ = me.group.position.z + Math.cos(yaw) * -dist;
                tempVec.set(camX, height, camZ);
                camera.position.lerp(tempVec, 0.18);
                camera.lookAt(me.group.position.x + Math.sin(yaw) * 60, 24, me.group.position.z + Math.cos(yaw) * 60);
                me.group.rotation.y = yaw;
            }
            // Update other players (rebuild on tier/weapon/hat/neck change)
            const otherAlpha = 1 - Math.pow(0.004, dt);
            const seenP = new Set();
            for (const p of s?.players || []) {
                seenP.add(p.uid);
                const pTier = p.tier || "rusty";
                const pWeapon = p.weapon || "bronze_sword";
                const pHat = p.hat || "";
                const pNeck = p.neck || "";
                let h = players.get(p.uid);
                if (!h || h.tier !== pTier || h.weapon !== pWeapon || h.hat !== pHat || h.neck !== pNeck) {
                    if (h) scene.remove(h.group);
                    h = buildVoxelCharacter(scene, false, pTier, pWeapon, pHat, pNeck);
                    h.group.userData = { entityType: "player", entityId: p.uid };
                    h.group.position.set(p.x, 0, p.y);
                    players.set(p.uid, h);
                }
                h.group.position.x += (p.x - h.group.position.x) * otherAlpha;
                h.group.position.z += (p.y - h.group.position.z) * otherAlpha;
                const hp = Math.max(0, (p.hp || 0) / (p.max_hp || 100));
                h.hpFill.scale.x = Math.max(0.001, hp);
                h.hpFill.position.x = (16 * hp - 16) / 2;
                h.hpFill.material.color.setHex(hp < 0.3 ? 0xef4444 : 0x22c55e);
                h.group.visible = p.alive !== false;
                // Update level label for other players
                const pLevel = p.level || 1;
                if (h.level !== pLevel) {
                    updateTextSprite(h.levelLabel, `Lv ${pLevel}`, {
                        color: "#fde047", outline: "#1a1410", size: 60, weight: "900",
                    });
                    h.level = pLevel;
                }
                // Detect a fresh melee swing on this remote player
                {
                    const cur = p.swing_age ?? 999;
                    const prev = lastSwingAge.get(p.uid) ?? 999;
                    if (cur < prev - 0.05 && cur < 0.4) triggerSwing(h);
                    lastSwingAge.set(p.uid, cur);
                }
                // Face direction of motion for remote players + run gait
                if (Math.abs(p.x - h.group.position.x) + Math.abs(p.y - h.group.position.z) > 0.5) {
                    const tx = p.x - h.group.position.x;
                    const tz = p.y - h.group.position.z;
                    if (tx * tx + tz * tz > 1) {
                        h.group.rotation.y = Math.atan2(tx, tz);
                    }
                }
                updateGait(h, dt);
                updateHitFlash(h, dt);
                updateChatBubble(h, p.chat);
            }
            for (const [uid, h] of players) {
                if (!seenP.has(uid)) { scene.remove(h.group); players.delete(uid); }
            }
            // Update mobs
            const seenM = new Set();
            for (const m of s?.mobs || []) {
                seenM.add(m.mid);
                let h = mobs.get(m.mid);
                if (!h) {
                    h = buildVoxelMob(scene, m.kind);
                    h.group.userData = { entityType: "mob", entityId: m.mid };
                    h.group.position.set(m.x, 0, m.y);
                    mobs.set(m.mid, h);
                }
                h.group.position.x += (m.x - h.group.position.x) * otherAlpha;
                h.group.position.z += (m.y - h.group.position.z) * otherAlpha;
                const hp = Math.max(0, m.hp / m.max_hp);
                h.hpFill.scale.x = Math.max(0.001, hp);
                h.hpFill.position.x = (14 * hp - 14) / 2;
                updateHitFlash(h, dt);
            }
            for (const [mid, h] of mobs) {
                if (!seenM.has(mid)) { scene.remove(h.group); mobs.delete(mid); }
            }
            // Update bullets
            const seenB = new Set();
            for (const b of s?.bullets || []) {
                seenB.add(b.bid);
                let m = bullets.get(b.bid);
                if (!m) {
                    m = buildBullet(scene, b.mine);
                    bullets.set(b.bid, m);
                }
                m.position.set(b.x, 16, b.y);
            }
            for (const [bid, m] of bullets) {
                if (!seenB.has(bid)) { scene.remove(m); bullets.delete(bid); }
            }
            // Update loot (with bob animation). Items vs credits use different meshes.
            const seenL = new Set();
            for (const l of s?.loot || []) {
                seenL.add(l.lid);
                let h = loots.get(l.lid);
                if (!h) {
                    if (l.slot && l.item) {
                        h = buildItemLoot(scene, l.slot, l.item);
                    } else {
                        h = buildLoot(scene);
                    }
                    loots.set(l.lid, h);
                }
                h.group.position.set(l.x, 0, l.y);
                if (h.core) {
                    h.core.rotation.y += dt * 2;
                    const baseY = h.kind === "item" ? 4 : 14;
                    h.core.position.y = baseY + Math.sin(now * 0.004 + l.x) * 2.5;
                }
            }
            for (const [lid, h] of loots) {
                if (!seenL.has(lid)) { scene.remove(h.group); loots.delete(lid); }
            }
            // Click-to-move target marker — pulses on the ground at the target XZ
            if (clickMarkerRef) {
                const tgt = clickMarkerRef.current;
                if (tgt) {
                    clickMarker.visible = true;
                    clickMarker.position.x = tgt.x;
                    clickMarker.position.z = tgt.y;
                    // Pulse scale + opacity
                    const pulse = 1 + Math.sin(now * 0.008) * 0.25;
                    clickMarker.scale.set(pulse, pulse, pulse);
                    clickMarker.material.opacity = 0.65 + Math.sin(now * 0.006) * 0.2;
                } else {
                    clickMarker.visible = false;
                }
            }

            // Attack-target marker — red ring under the targeted entity (player or mob)
            if (attackTargetRef) {
                const tgt = attackTargetRef.current;
                let pos = null;
                if (tgt) {
                    if (tgt.type === "player") {
                        const h = players.get(tgt.id);
                        if (h) pos = h.group.position;
                        // If clicked on self, fall back to me
                        else if (me && me.group.userData?.entityId === tgt.id) pos = me.group.position;
                    } else if (tgt.type === "mob") {
                        const h = mobs.get(tgt.id);
                        if (h) pos = h.group.position;
                    }
                }
                if (pos) {
                    targetMarker.visible = true;
                    targetMarker.position.x = pos.x;
                    targetMarker.position.z = pos.z;
                    const tp = 1 + Math.sin(now * 0.012) * 0.18;
                    targetMarker.scale.set(tp, tp, tp);
                } else {
                    targetMarker.visible = false;
                    // If the targeted mob no longer exists, clear the ref
                    if (tgt && tgt.type === "mob" && !mobs.has(tgt.id)) attackTargetRef.current = null;
                }
            }
            // Floating damage numbers — spawn on new events, animate (rise + fade)
            for (const ev of s?.hits || []) {
                if (!hits.has(ev.eid)) {
                    // Hot pink for hits, hot yellow for big hits (>= 24 base dmg before mult)
                    const big = ev.dmg >= 24;
                    const text = `-${ev.dmg}`;
                    const sprite = makeTextSprite(text, {
                        color: big ? "#fde047" : "#fecaca",
                        outline: "#7f1d1d",
                        size: big ? 78 : 64,
                        weight: "900",
                    });
                    sprite.scale.set(big ? 32 : 26, big ? 12 : 10, 1);
                    // Small horizontal jitter so stacked hits don't overlap perfectly
                    const jitter = (Math.random() - 0.5) * 12;
                    sprite.position.set(ev.x + jitter, 36, ev.y);
                    scene.add(sprite);
                    hits.set(ev.eid, { sprite, age: 0, maxAge: 0.9, vx: jitter * 0.6 });
                    // Hit-flash + recoil on the struck entity (mob or player)
                    let victim = null;
                    if (ev.target_mid) victim = mobs.get(ev.target_mid);
                    else if (ev.target_uid) {
                        victim = players.get(ev.target_uid);
                        if (!victim && me && me.group.userData?.entityId === ev.target_uid) victim = me;
                    }
                    if (victim && victim.group) {
                        victim.hitFlash = { t: 0, dur: 0.18 };
                    }
                }
            }
            for (const [eid, h] of hits) {
                h.age += dt;
                if (h.age >= h.maxAge) {
                    scene.remove(h.sprite);
                    h.sprite.material.map?.dispose?.();
                    h.sprite.material.dispose?.();
                    hits.delete(eid);
                    continue;
                }
                // Rise upward, slight fade
                h.sprite.position.y += dt * 38;
                const t = h.age / h.maxAge;
                h.sprite.material.opacity = Math.max(0, 1 - t * 1.1);
                // Slight outward drift
                h.sprite.position.x += h.vx * dt * 0.5;
            }

            renderer.render(scene, camera);
            requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        return cleanup;
    }, [stateRef, aimRef, cameraYawRef, pickerRef, clickMarkerRef, entityPickerRef, attackTargetRef]);

    return (
        <div
            ref={containerRef}
            data-testid="mmo-3d-scene"
            style={{ width: "100%", height: "100%", display: "block", background: "#c8c8a8" }}
        />
    );
}
